import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Country } from '@acs/shared';
import { Banner, BrandLoader, Button, CopyButton, Icon, Tabs } from '@acs/ui';
import { friendlyError } from '../../shared/api/errors.js';
import { formatPhoneDisplay } from '../../shared/lib/phoneFormat.js';
import type { BusinessDoc } from '../../shared/api/converters.js';
import { replaceCachedBusiness } from '../../shared/api/businessCache.js';
import { reconnectFirestore } from '../../shared/api/network.js';
import { fetchBusinessFromServer } from '../../shared/api/serverRead.js';
import {
  declaredSourceNumber,
  hasSourceMismatch,
  isCarrierUnsupported,
  shouldShowForwardingTarget,
} from '../../shared/lib/businessNumber.js';
import { useBeginForwardingTest, useCancelForwardingTest, useSaveRegistration } from './hooks.js';
import {
  DIVERT_ORDER,
  ForwardingCost,
  divertOptions,
  operatorHelp,
  type DivertKind,
  type DivertOption,
} from './operatorForwarding.js';
import { useI18n } from '../../i18n/index.js';

/**
 * The forwarding experience — one component, rendered identically by the setup
 * wizard's number step and by Settings › Number.
 *
 * The two used to be separate compositions over a shared instructions block, and
 * they had drifted into saying the same things a different number of times: the
 * forwarding target appeared three times in Settings, the "don't advertise this
 * number" warning twice, and the verification step told the owner to do
 * something subtly different in each place. Four dial codes were listed side by
 * side with no indication which one to use.
 *
 * What replaces it is two numbered steps — set the divert, then prove it
 * landed — with the choice inside the first laid out as tabs: five named
 * outcomes, one dial code on screen at a time, the gentlest one first. The rest
 * (the cost table, the bare target number for carrier apps) lives one
 * disclosure away. Once the line is proven, {@link ForwardingActive} takes over.
 *
 * The owner's own line belongs to the HOST, not to this panel: both hosts print
 * it above, with an editor of their own, and this panel is about the divert onto
 * it. It shares their verification window so that editor freezes mid-check.
 */
export function ForwardingPanel({
  business,
  checkWindow: hostWindow,
}: {
  business: BusinessDoc;
  /** The host's verification window, when the host needs the same answer this
   * panel does — both hosts freeze their line field while a check runs, and a
   * second copy of the state would disagree with this one after a cancel. */
  checkWindow?: ForwardingWindow;
}) {
  const { t } = useI18n();
  const fwd = business.forwardNumber;
  const declared = formatPhoneDisplay(declaredSourceNumber(business));

  // Owner's mobile operator: persisted so the cost note (and any operator-
  // specific dial code) survives a reload. Optimistic locally — the doc is
  // live-subscribed, so the write comes back and confirms itself.
  const saveOperator = useSaveRegistration(business.id);
  const [operatorId, setOperatorId] = useState<string | null>(business.ownerLineOperator);
  useEffect(() => setOperatorId(business.ownerLineOperator), [business.ownerLineOperator]);
  const selectOperator = (id: string) => {
    setOperatorId(id);
    saveOperator.mutate({ ownerLineOperator: id });
  };

  // Is a verification window open right now? Read here as well as inside the
  // check itself, because it is what freezes the line field above: a check in
  // flight is a statement about one specific number (see `cancelForwardingTest`
  // on the server, which enforces the same rule against a second tab).
  // One window, whoever owns it. When the host passed its own, the instance
  // here is inert — it derives from the same live doc, so it costs a countdown
  // nobody reads rather than a second opinion about whether a check is running.
  const ownWindow = useForwardingWindow(business);
  const checkWindow = hostWindow ?? ownWindow;

  if (!fwd) return null;

  const guide = (
    <ForwardingGuide
      country={business.country}
      target={fwd.phoneNumber}
      operatorId={operatorId}
      onOperator={selectOperator}
    />
  );

  // Whether our forwarding number is allowed on screen at all (doc 18 §7.4) —
  // the same rule Settings applied before this component absorbed it, stated
  // once in `shouldShowForwardingTarget`. Once a forwarded call has proved the
  // line, the numbered setup drops away: what is left is one green line and the
  // controls for changing it.
  if (!shouldShowForwardingTarget(business)) {
    return (
      <ForwardingActive declared={declared} guide={guide} />
    );
  }

  // Two steps, and the declared line is not one of them. It used to be step 1,
  // "Confirm your business number", carrying a field — but both hosts already
  // print that number and an editor for it, immediately above this panel, so
  // the step asked the owner to confirm something they were looking at and
  // offered a second field for it. What is left is what this panel is actually
  // for: set the divert, then prove it landed.
  return (
    <div className="fwd">
      <FwdStep n={1} title={t('fwd.step1')}>{guide}</FwdStep>
      <FwdStep n={2} title={t('fwd.step2')}>
        <CheckOutcome business={business} declared={declared} />
        <ForwardingCheck business={business} declared={declared} checkWindow={checkWindow} />
      </FwdStep>
    </div>
  );
}

/** One numbered step of the forwarding flow. */
function FwdStep({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <section className="fwd-step">
      <header className="fwd-step__head">
        <span className="fwd-step__mark" aria-hidden>
          {n}
        </span>
        <h3 className="fwd-step__title">{title}</h3>
      </header>
      <div className="fwd-step__body">{children}</div>
    </section>
  );
}

/**
 * Step 1: pick what should reach the assistant, then dial one code.
 *
 * Every divert is a tab, "Every call" first — so the whole set is visible at a
 * glance while exactly one dial code is ever on screen. The previous shape put
 * two options in a toggle and folded the other three into a disclosure, which
 * kept the screen calm but made three of the five choices something an owner
 * had to go looking for.
 *
 * The forwarding target is shown ONCE, inside the code the owner dials — never
 * as a headline number of its own, which reads as "here is your new business
 * number" and ends up printed on a shopfront (doc 18 §7.4). The bare number
 * stays available for owners who set the divert in their carrier's app, with
 * the caveat attached to it rather than floating in its own banner.
 */
export function ForwardingGuide({
  country,
  target,
  operatorId,
  onOperator,
}: {
  country: Country;
  /** Our assigned forward-target number. */
  target: string;
  operatorId: string | null;
  onOperator: (id: string) => void;
}) {
  const { t, tn } = useI18n();
  // Lands on the first tab: handing the whole line over is what an owner
  // setting up a receptionist usually means (see `DIVERT_ORDER`).
  const [kind, setKind] = useState<DivertKind>(DIVERT_ORDER[0]);
  const carrier = operatorHelp(country, operatorId);

  return (
    <>
      <Tabs
        aria-label={t('fwd.tabsLabel')}
        variant="pill"
        value={kind}
        onChange={(v) => setKind(v as DivertKind)}
        className="fwd-tabs"
        items={divertOptions(operatorId, country, target, t).map((option) => ({
          value: option.kind,
          label: option.tab,
          content: <DivertPanel option={option} />,
        }))}
      />

      <ForwardingCost country={country} selectedId={operatorId} onSelect={onOperator} />

      <details className="fwd-details">
        <summary className="fwd-details__summary">
          <span>{t('fwd.carrierApp')}</span>
          <span className="fwd-details__meta">{t('fwd.carrierAppMeta')}</span>
        </summary>
        <div className="fwd-details__body">
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>
            {tn('fwd.carrierAppBody', {
              target: <span className="mono">{target}</span>,
              copy: <CopyButton value={target} label={t('fwd.copyTarget')} />,
            })}
            {carrier &&
              tn('fwd.carrierGuide', {
                operator: carrier.name,
                link: (
                  <a href={carrier.href} target="_blank" rel="noopener noreferrer">
                    {t('fwd.carrierGuideLink')}
                  </a>
                ),
              })}
          </p>
        </div>
      </details>
    </>
  );
}

/**
 * One tab's worth: what the choice means, and the single code that sets it.
 *
 * Nothing under the code. It used to carry a standing instruction — dial it on
 * your own line, exactly as shown, your carrier answers with a confirmation,
 * reversible any time — which is four sentences of how-to under every one of
 * the five tabs, on a screen an owner also revisits months later to re-point a
 * working divert. A dial code next to a copy button already reads as something
 * to dial, and the step's own heading says on which line.
 */
function DivertPanel({ option }: { option: DivertOption }) {
  const { t } = useI18n();
  return (
    <>
      <p className="fwd-choice muted">{option.description}</p>

      <div className="fwd-code">
        <code className="mono fwd-code__value">{option.code}</code>
        <CopyButton value={option.code} label={t('fwd.copyCode', { label: option.label })} size="md" />
      </div>
    </>
  );
}

/**
 * Whether a verification window is open, and how long is left of it.
 *
 * The window is the server's (`forwardNumber.testExpiresAt`, live-subscribed),
 * not a local flag — a reload used to lose "we're waiting for your call" while
 * the window was still open, and the owner would start a second one. The local
 * deadline covers the second or two before the write echoes back, and the
 * local *override* covers the same gap after a cancel, where the server value
 * is still the old deadline.
 *
 * Lifted out of the check itself, and exported, because the host needs the same
 * answer: the check renders the countdown, and the host's line field is frozen
 * while this is true. One instance, shared, so a cancel cannot leave the two
 * disagreeing.
 */
export function useForwardingWindow(business: BusinessDoc): ForwardingWindow {
  const [localExpiry, setLocalExpiry] = useState<number | null>(null);
  const [closedLocally, setClosedLocally] = useState(false);

  const serverExpiry = business.forwardNumber?.testExpiresAt?.getTime() ?? null;
  // The gateway stamps `verifiedAt` on every call it consumes as a check,
  // whatever the outcome. When that lands, the check is over: drop the local
  // deadline, or it keeps the "waiting for your call" spinner running for the
  // rest of the window over an outcome that has already arrived. Invisible on
  // success — the panel turns green and unmounts this — which is why it only
  // showed up once a check could end in something other than success.
  const consumedAt = business.forwardNumber?.verifiedAt?.getTime() ?? null;
  useEffect(() => {
    if (consumedAt != null) {
      setLocalExpiry(null);
      setClosedLocally(false);
    }
  }, [consumedAt]);
  // A new window from the server clears a stale local close — otherwise
  // "start over" right after a cancel would render as not waiting.
  useEffect(() => {
    setClosedLocally(false);
  }, [serverExpiry]);

  const expiry = closedLocally ? null : Math.max(serverExpiry ?? 0, localExpiry ?? 0) || null;
  const remaining = useCountdown(expiry);
  return {
    remaining,
    waiting: remaining > 0,
    open: (expiresInSec: number) => {
      setClosedLocally(false);
      setLocalExpiry(Date.now() + expiresInSec * 1000);
    },
    close: () => {
      setLocalExpiry(null);
      setClosedLocally(true);
    },
  };
}

/** How often an open check asks the server directly — a confirmation lands
 *  within this long even when the live listener has died. Bounded by the
 *  five-minute window and by the tab being visible: at most a hundred
 *  one-document reads per check, and none while nobody is looking. */
const CHECK_POLL_MS = 3000;

/**
 * While a check is open, make sure its outcome reaches the screen.
 *
 * The outcome is a server write (the gateway closes the window on the owner's
 * call) and normally arrives through the live listener on the businesses list.
 * Owners kept seeing "waiting for your call" after a successful call until they
 * reloaded: the call itself is what takes the page away — a phone suspends the
 * tab, the browser freezes the background tab that holds the multi-tab
 * connection — and the listener can come back silently dead. So for exactly as
 * long as a check runs, the business is also read straight from the server
 * every few seconds while the tab is visible, and returning to the tab reads at
 * once. A server copy newer than the cache is proof the listener is behind, so
 * that also reconnects it: patching the cache alone would leave the next local
 * write to re-emit the stale snapshot over the confirmation.
 */
function useCheckOutcomeWatch(business: BusinessDoc, waiting: boolean): void {
  const qc = useQueryClient();
  const { id, ownerUid } = business;

  useEffect(() => {
    if (!waiting) return;
    let cancelled = false;
    let inFlight = false;
    const poll = async () => {
      if (inFlight || document.visibilityState !== 'visible') return;
      inFlight = true;
      try {
        const fresh = await fetchBusinessFromServer(id);
        if (!cancelled && fresh && replaceCachedBusiness(qc, ownerUid, fresh)) void reconnectFirestore();
      } catch {
        /* offline or refused — the next tick asks again */
      } finally {
        inFlight = false;
      }
    };
    const onReturn = () => void poll();

    const timer = window.setInterval(() => void poll(), CHECK_POLL_MS);
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [waiting, id, ownerUid, qc]);
}

export interface ForwardingWindow {
  /** Seconds left, 0 when no window is open. */
  remaining: number;
  waiting: boolean;
  /** Optimistically open the window for `expiresInSec`. */
  open: (expiresInSec: number) => void;
  /** Optimistically close it, ahead of the server write echoing back. */
  close: () => void;
}

/**
 * Step 3: the owner calls their own line and we confirm the divert landed.
 *
 * "Stop the check" is not decoration. The declared line is frozen while a
 * check runs, so without a way to close the window early an owner who spots a
 * typo mid-check would have to wait out the five minutes — or place a call they
 * already know will be recorded as a mismatch.
 */
export function ForwardingCheck({
  business,
  declared,
  checkWindow,
}: {
  business: BusinessDoc;
  declared: string | null;
  checkWindow: ForwardingWindow;
}) {
  const { t, tn } = useI18n();
  const beginTest = useBeginForwardingTest();
  const cancelTest = useCancelForwardingTest();
  const { remaining, waiting } = checkWindow;
  useCheckOutcomeWatch(business, waiting);

  return (
    <>
      {waiting ? (
        <>
          <p className="fwd-wait">
            <BrandLoader size={24} />
            <span>
              <strong>{t('fwd.callNow', { number: declared ?? t('fwd.yourBusinessNumber') })}</strong>
              {t('fwd.callNowSuffix')}
            </span>
            <span className="fwd-wait__clock mono">{formatCountdown(remaining)}</span>
          </p>
          <p className="fwd-note muted">
            {t('fwd.letItRing')}
          </p>
        </>
      ) : (
        <p className="fwd-note muted" style={{ marginTop: 0 }}>
          {declared ? (
            tn('fwd.onceSet', { number: <span className="mono">{declared}</span> })
          ) : (
            t('fwd.addNumberFirst')
          )}
        </p>
      )}

      <div className="row" style={{ gap: 8 }}>
        <Button
          variant={waiting ? 'ghost' : 'primary'}
          loading={beginTest.isPending}
          disabled={!declared || cancelTest.isPending}
          leadingIcon={<Icon name="phone" size={16} />}
          onClick={() =>
            beginTest.mutate(business.id, {
              onSuccess: (res) => checkWindow.open(res.expiresInSec),
            })
          }
        >
          {t(waiting ? 'fwd.startOver' : 'fwd.startCheck')}
        </Button>

        {/* Only while something is running: a stop button with nothing to stop
            is a question the owner has to answer before they can ignore it. */}
        {waiting && (
          <Button
            variant="ghost"
            loading={cancelTest.isPending}
            onClick={() =>
              cancelTest.mutate(business.id, { onSuccess: () => checkWindow.close() })
            }
          >
            {t('fwd.stopCheck')}
          </Button>
        )}
      </div>

      {cancelTest.isError && (
        <Banner tone="danger">
          {friendlyError(t, cancelTest.error, t('fwd.stopFailed'))}
        </Banner>
      )}

      {beginTest.isError && (
        <Banner tone="danger">
          {friendlyError(t, beginTest.error, t('fwd.checkFailed'))}
        </Banner>
      )}
    </>
  );
}

/**
 * What the last verification call proved, when it proved something other than
 * the owner's line (doc 18 D3).
 *
 * Two outcomes, told apart because they need opposite answers. A MISMATCH is
 * the owner's to fix — nearly always a typo — so it shows both numbers side by
 * side, which is far easier to compare than the gateway reading digits aloud. An
 * UNSUPPORTED carrier is not theirs to fix, and is said plainly: we cannot
 * answer this line today.
 *
 * Both used to be one state rendered as SUCCESS — a green "live" with a note
 * promising a forwarding line "reserved for you alone". No such line was ever
 * reserved, and the assistant answered nothing.
 */
function CheckOutcome({ business, declared }: { business: BusinessDoc; declared: string | null }) {
  const { t, tn } = useI18n();
  const mine = <span className="mono">{declared ?? t('fwd.yourBusinessNumber')}</span>;

  if (hasSourceMismatch(business)) {
    const observed = formatPhoneDisplay(business.forwardNumber?.observedSourceNumber);
    return (
      <Banner tone="warning" title={t('fwd.mismatch.title')}>
        {observed
          ? tn('fwd.mismatch.body', { observed: <span className="mono">{observed}</span>, declared: mine })
          : tn('fwd.mismatch.bodyNoNumber', { declared: mine })}
      </Banner>
    );
  }
  if (isCarrierUnsupported(business)) {
    return (
      <Banner tone="warning" title={t('fwd.unsupported.title')}>
        {t('fwd.unsupported.body')}
      </Banner>
    );
  }
  return null;
}

/**
 * The finished state, in both hosts: one green line, then everything needed to
 * change it. Only ever rendered for a PROVEN line (`isForwardingProven`).
 *
 * The "change or turn off" block is NOT a disclosure any more. Folding it away
 * hid the thing an owner comes back to this page for — re-pointing the divert
 * after they have touched it on their phone — behind a summary that reads like
 * a footnote. A working setup is worth one green line, not three quarters of
 * the panel, so the rest of the card may as well be the controls.
 *
 * There is no re-check button here. A proven line has nothing left to prove,
 * and offering to check it again invited owners to re-run a five-minute
 * verification against a setup that was already working. The check comes back
 * on its own when it means something: changing the declared number drops the
 * proof server-side, this component unmounts, and the numbered steps ask for
 * one check on the NEW line.
 *
 * The declared line is NOT edited here. It is edited in the card above, which
 * is the one that prints it; this block is about the divert. Changing it there
 * drops the old proof and the routing grant with it server-side, so this whole
 * component unmounts in favour of the numbered steps and the new line has to be
 * proven on its own.
 */
export function ForwardingActive({
  declared,
  guide,
}: {
  declared: string | null;
  guide: React.ReactNode;
}) {
  const { t, tn } = useI18n();
  return (
    <div className="fwd">
      <p className="fwd-ok">
        <span className="fwd-ok__mark" aria-hidden>
          <Icon name="check" size={16} strokeWidth={3} />
        </span>
        <span>
          <strong>{t('fwd.live')}</strong>
          {tn('fwd.liveDetail', {
            number: <span className="mono">{declared ?? t('fwd.yourBusinessNumber')}</span>,
          })}
        </span>
      </p>

      <section className="fwd-manage">
        <header className="fwd-manage__head">
          <h3 className="fwd-manage__title">{t('fwd.changeOrTurnOff')}</h3>
          <span className="fwd-manage__meta">{t('fwd.dialCodes')}</span>
        </header>
        <p className="fwd-note muted">{t('fwd.manageHint')}</p>
        {guide}
      </section>
    </div>
  );
}

/** Seconds left until `deadline` (epoch ms), ticking once a second; 0 when past or unset. */
function useCountdown(deadline: number | null): number {
  const left = () => (deadline ? Math.max(0, Math.ceil((deadline - Date.now()) / 1000)) : 0);
  const [seconds, setSeconds] = useState(left);

  useEffect(() => {
    setSeconds(left());
    if (!deadline || deadline <= Date.now()) return;
    const id = window.setInterval(() => {
      const next = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setSeconds(next);
      if (next === 0) window.clearInterval(id);
    }, 1000);
    return () => window.clearInterval(id);
    // `left` closes over `deadline`, which is the only input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadline]);

  return seconds;
}

/** `4:07` — a countdown the owner can read at a glance while holding a phone. */
export function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
